/**
 * useLeads — super-admin inbox of landing-page inquiries (public.leads).
 *
 * Leads have no anon/authenticated RLS policy (service_role only — see
 * 20260508000004_leads.sql), so this hook does NOT query the table
 * directly. It invokes the report-leads Edge Function, which gates on
 * super_admin and reads with service_role. The hook is enabled only for
 * super_admin so non-operators never burn the network round trip or the
 * Edge Function's rate-limit budget.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useAuthStore, selectRole }       from '@/store/authStore'
import { AppError }                       from '@/lib/errors/AppError'

export type LeadVertical    = 'dorm' | 'office' | 'property_mgmt'
export type LeadInquiryType = 'info' | 'quote' | 'demo' | 'waitlist'
export type LeadGradeStatus = 'graded' | 'unavailable' | 'malformed'

export interface LeadRow {
  id:               string
  email:            string
  name:             string | null
  company:          string | null
  vertical:         LeadVertical
  inquiry_type:     LeadInquiryType
  message:          string | null
  rooms_count:      number | null
  properties_count: number | null
  utm_source:       string | null
  utm_medium:       string | null
  utm_campaign:     string | null
  source:           string
  fit_score:        number | null
  fit_reason:       string | null
  grade_status:     LeadGradeStatus | null
  created_at:       string
}

interface LeadsFnResponse {
  range_days: number
  leads:      LeadRow[]
}

export const DEFAULT_LEADS_RANGE_DAYS = 90

export function useLeads(rangeDays: number = DEFAULT_LEADS_RANGE_DAYS): UseQueryResult<LeadRow[]> {
  const supabase = useSupabase()
  // Read the role straight from the store selector (not the useAuth
  // facade) so this data hook stays decoupled from the auth lifecycle
  // module — same server-controlled app_metadata read, fewer imports.
  const role     = useAuthStore(selectRole)

  return useQuery({
    queryKey:  ['leads', rangeDays, role],
    staleTime: 60_000,
    enabled:   role === 'super_admin',
    queryFn: async (): Promise<LeadRow[]> => {
      const { data, error } = await supabase.functions.invoke<LeadsFnResponse>('report-leads', {
        body: { range_days: rangeDays },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { detail?: string }
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code:        'LEADS_FETCH_FAILED',
          message:     detail,
          userMessage: 'Could not load leads.',
          status:      500,
          cause:       error,
        })
      }
      if (!data) {
        throw new AppError({
          code:        'EMPTY_RESPONSE',
          message:     'No data',
          userMessage: 'The server returned no result.',
          status:      500,
        })
      }
      return data.leads
    },
  })
}
